import { useRef } from 'react';
import type { Locale } from '@/strings/types';
import { useShellLocale } from './shellLocale';
import { IconMinutes } from './icons';
import useAutoScroll from './useAutoScroll';
import { useScrollFade } from './useScrollFade';
import './card.css';
import './MinutesView.css';

/**
 * Tab 1 — Minutes Summary.
 *
 * CONTENT POLICY: every word below is synthetic. The owner-supplied examples
 * used to derive this shape describe real meetings with real people, a real
 * client, a live legal dispute, and real figures. None of that may appear in a
 * public marketing asset. What is borrowed is the STRUCTURE the product
 * actually emits — Executive Summary, Key Points, Decisions Made, Action Items,
 * Risks & Issues, Next Steps — and the register those sections are written in.
 *
 * THE ARABIC IS A TRANSLATION OF THE SAME MEETING, not a second meeting. A
 * visitor switching locales should see one workspace in two languages, which is
 * also why the owner names, dates and priorities line up row for row with
 * ActionsView's.
 *
 * ⚠️ AR COPY IS A PROPOSAL PENDING OWNER SIGN-OFF (style guide §"Workflow", step
 * 7: "owner verifies all Arabic at the end; wording locks only then"). It is
 * written to docs/arabic-style-guide.md — verb-first (§2), true passive rather
 * than «تم» (§3), مصدر for the listed steps (§3), «و» for list joins and Arabic
 * commas (§2/§7), Western digits with spelled-out months (§7).
 */

type PriKey = 'high' | 'medium' | 'low';

interface ActionRow {
  summary: string;
  due: string;
  /** The CSS tone key, kept SEPARATE from the label — see the note at render. */
  pri: PriKey;
  who: string;
}

interface MinutesCopy {
  cardTitle: string;
  edit: string;
  copy: string;
  regionLabel: string;
  h: {
    summary: string;
    key: string;
    decisions: string;
    actions: string;
    risks: string;
    next: string;
  };
  cols: { summary: string; due: string; pri: string; who: string };
  pri: Record<PriKey, string>;
  lead: string;
  keyPoints: readonly string[];
  decisions: readonly string[];
  actions: readonly ActionRow[];
  risks: readonly string[];
  nextSteps: readonly string[];
}

/* Trimmed 2026-09-05 to buy vertical room for Next Steps. Removals were the
   redundant lines rather than arbitrary ones: the dropped key point about the
   space being too small restated a risk, and the dropped decision about food
   and beverage restated a key point. */
const COPY: Record<Locale, MinutesCopy> = {
  en: {
    cardTitle: 'Minutes Summary',
    edit: 'Edit',
    copy: 'Copy',
    regionLabel: 'Minutes summary',
    h: {
      summary: 'Executive Summary',
      key: 'Key Points',
      decisions: 'Decisions Made',
      actions: 'Action Items',
      risks: 'Risks & Issues',
      next: 'Next Steps',
    },
    cols: { summary: 'Summary', due: 'Deadline', pri: 'Priority', who: 'Responsible' },
    pri: { high: 'High', medium: 'Medium', low: 'Low' },
    lead:
      'The team shaped a lightweight National Day activation with the emphasis on decor and ' +
      'visual presentation rather than a large-scale guest experience. The concept centres on ' +
      'heritage crafts, a henna station, a photo booth, and a short storytelling piece, with ' +
      'food and beverage remaining with the venue. Live elements such as a traditional ' +
      'performance or projection mapping were treated as optional, depending on budget.',
    keyPoints: [
      'The activation is small in scale and tied to National Day.',
      'The venue handles food and beverage, so it falls outside our scope.',
      'Interactive elements: heritage crafts, children’s activities, henna, and a photo booth.',
      'Long-form activities are a poor fit; visitors are expected to pass through quickly.',
      'A short storytelling piece was proposed for visitors from outside the Kingdom.',
      'Decor is the stated priority, particularly for photographed reporting.',
    ],
    decisions: [
      'Focus the proposal on a simple, visually strong activation rather than a large event concept.',
      'Present storytelling as three options: a narrator, a short film, or projection mapping.',
      'Include a live performance option to meet the stated request.',
    ],
    /* Three owners, two priorities — the shape that shows the extraction doing
       real work. Dropping the fourth row kept all three names distinct. */
    actions: [
      { summary: 'Follow up on outstanding scope questions', due: '10 Sep', pri: 'high', who: 'Khalid M.' },
      { summary: 'Price the agreed craft and decor elements', due: '15 Sep', pri: 'high', who: 'Sara T.' },
      { summary: 'Identify a decor designer for the setup', due: '15 Sep', pri: 'medium', who: 'Reem A.' },
    ],
    risks: [
      'Scope remains underdefined, which makes pricing hard to finalize.',
      'The venue may be too small for bulky installations.',
      'Decor expectations may exceed the likely budget.',
    ],
    nextSteps: [
      'Confirm the core concept and keep the activation simple and visually strong.',
      'Seek final clarification on scope and budget direction.',
      'Price the proposed elements, including crafts and the storytelling format.',
      'Submit the proposal on current assumptions if no further input arrives.',
    ],
  },
  ar: {
    cardTitle: 'ملخص المحضر',
    edit: 'تحرير',
    copy: 'نسخ',
    regionLabel: 'ملخص المحضر',
    h: {
      summary: 'الملخص التنفيذي',
      key: 'النقاط الرئيسية',
      decisions: 'القرارات المتخذة',
      actions: 'المهام والإجراءات',
      risks: 'المخاطر والمشكلات',
      next: 'الخطوات التالية',
    },
    cols: { summary: 'الملخص', due: 'الموعد النهائي', pri: 'الأولوية', who: 'المسؤول' },
    pri: { high: 'عالية', medium: 'متوسطة', low: 'منخفضة' },
    /* «أما... فـ» for the contrast in the last sentence, and «لا على» for the
       one in the first: both are §2 connectors doing work an English period
       does with a new sentence. The list joins take «و» rather than commas. */
    lead:
      'صاغ الفريق فعالية خفيفة لليوم الوطني يتركز اهتمامها على الديكور والعرض البصري لا على ' +
      'تجربة ضيافة واسعة. ويقوم التصور على الحرف اليدوية وركن الحناء وركن التصوير ومادة سردية ' +
      'قصيرة، وتبقى المأكولات والمشروبات على عاتق المكان. أما العناصر الحية كالعرض التراثي أو ' +
      'الإسقاط الضوئي فعُدت خيارات مرتبطة بالميزانية.',
    keyPoints: [
      'الفعالية صغيرة النطاق ومرتبطة باليوم الوطني.',
      'يتولى المكان المأكولات والمشروبات، فهي خارج نطاقنا.',
      'العناصر التفاعلية: الحرف اليدوية وأنشطة الأطفال والحناء وركن التصوير.',
      'الأنشطة الطويلة غير مناسبة؛ إذ يمر الزوار مرورا سريعا.',
      'اقتُرحت مادة سردية قصيرة للزوار القادمين من خارج المملكة.',
      'الديكور هو الأولوية المعلنة، وخاصة في التغطية المصورة.',
    ],
    /* مصدر, not imperatives: §3 rules that a listed or interface-level command
       forces a gender on every reader, so a numbered list of decisions takes
       the verbal noun. Same for Next Steps below. */
    decisions: [
      'تركيز العرض على فعالية بسيطة قوية بصريا بدلا من تصور حدث كبير.',
      'تقديم السرد في ثلاثة خيارات: رواية مباشرة أو فيلم قصير أو إسقاط ضوئي.',
      'إدراج خيار عرض حي استجابة للطلب المعلن.',
    ],
    actions: [
      { summary: 'متابعة الأسئلة المعلقة حول النطاق', due: '10 سبتمبر', pri: 'high', who: 'خالد المطيري' },
      { summary: 'تسعير عناصر الحرف والديكور المتفق عليها', due: '15 سبتمبر', pri: 'high', who: 'سارة التميمي' },
      { summary: 'تحديد مصمم ديكور للتنفيذ', due: '15 سبتمبر', pri: 'medium', who: 'ريم الحربي' },
    ],
    risks: [
      'لا يزال النطاق غير محدد بدقة، ما يصعب إنهاء التسعير.',
      'قد يضيق المكان عن التركيبات الكبيرة.',
      'قد تتجاوز توقعات الديكور الميزانية المرجحة.',
    ],
    nextSteps: [
      'تأكيد التصور الأساسي والإبقاء على الفعالية بسيطة قوية بصريا.',
      'طلب توضيح نهائي للنطاق ووجهة الميزانية.',
      'تسعير العناصر المقترحة، ومنها الحرف وصيغة السرد.',
      'تقديم العرض على الافتراضات الحالية إن لم ترد إفادة إضافية.',
    ],
  },
};

export default function MinutesView() {
  const bodyRef = useRef<HTMLDivElement>(null);
  useAutoScroll(bodyRef);
  useScrollFade(bodyRef);
  const t = COPY[useShellLocale()];

  return (
    <article className="pncard">
      <header className="pncard__head">
        <span className="pncard__head-title">
          <IconMinutes size={16} />
          {t.cardTitle}
        </span>
        <span className="pncard__head-actions">
          <span className="pncard__btn">{t.edit}</span>
          <span className="pncard__btn">{t.copy}</span>
        </span>
      </header>

      {/* tabIndex makes the scroll region reachable by keyboard — a scrollable
          box that only a mouse can reach is not scrollable for everyone. */}
      <div
        className="pncard__body pnmin__body pn-scroll"
        ref={bodyRef}
        tabIndex={0}
        role="region"
        aria-label={t.regionLabel}
      >
        <h3 className="pnmin__h">{t.h.summary}</h3>
        <p className="pnmin__p">{t.lead}</p>

        <h3 className="pnmin__h">{t.h.key}</h3>
        <ul className="pnmin__list">
          {t.keyPoints.map((k) => (
            <li key={k}>{k}</li>
          ))}
        </ul>

        <h3 className="pnmin__h">{t.h.decisions}</h3>
        <ol className="pnmin__list pnmin__list--num">
          {t.decisions.map((d) => (
            <li key={d}>{d}</li>
          ))}
        </ol>

        <h3 className="pnmin__h">{t.h.actions}</h3>
        <table className="pnmin__table">
          <thead>
            <tr>
              <th>{t.cols.summary}</th>
              <th>{t.cols.due}</th>
              <th>{t.cols.pri}</th>
              <th>{t.cols.who}</th>
            </tr>
          </thead>
          <tbody>
            {t.actions.map((a) => (
              <tr key={a.summary}>
                <td>{a.summary}</td>
                <td className="pnmin__td-meta">{a.due}</td>
                <td>
                  {/* data-pri takes the KEY, never the label. It used to be
                      `a.pri.toLowerCase()` off the visible string, which works
                      only while the visible string is English — in Arabic every
                      row would have fallen through to the default tone. */}
                  <span className="pnmin__pri" data-pri={a.pri}>
                    {t.pri[a.pri]}
                  </span>
                </td>
                <td className="pnmin__td-meta">{a.who}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <h3 className="pnmin__h">{t.h.risks}</h3>
        <ul className="pnmin__list">
          {t.risks.map((r) => (
            <li key={r}>{r}</li>
          ))}
        </ul>

        <h3 className="pnmin__h">{t.h.next}</h3>
        <ol className="pnmin__list pnmin__list--num">
          {t.nextSteps.map((s) => (
            <li key={s}>{s}</li>
          ))}
        </ol>
      </div>
    </article>
  );
}
